import React, { useState, useEffect, useCallback } from "react";
import { Button, Modal } from "react-bootstrap";
import PhotoGalleryService from "../../../_services/PhotoGalleryService";
import PhotoUploadZone from "./PhotoUploadZone";
import PhotoGrid from "./PhotoGrid";
import TaggingPanel from "./TaggingPanel";
import AddToAlbumDialog from "./AddToAlbumDialog";
import ShareToVoilaBoardDialog from "./ShareToVoilaBoardDialog";

interface Album {
  id: number;
  name: string;
  description: string | null;
  coverPhotoUrl: string | null;
  isPublished: number;
}

interface Photo {
  id: number;
  s3Url: string;
  thumbnailUrl: string | null;
  originalFilename: string | null;
  caption: string | null;
}

interface Props {
  album: Album;
  onAlbumUpdated: () => void;
}

const AlbumPhotosView: React.FC<Props> = ({ album, onAlbumUpdated }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [viewingPhoto, setViewingPhoto] = useState<Photo | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Photo | null>(null);
  const [showAddToAlbum, setShowAddToAlbum] = useState(false);
  const [showShareToVoilaBoard, setShowShareToVoilaBoard] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const loadPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await PhotoGalleryService.getAlbumPhotos(album.id, 1, 200);
      setPhotos(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load photos", err);
    } finally {
      setLoading(false);
    }
  }, [album.id]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const handleToggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedIds(new Set(photos.map((p) => p.id)));
  };

  const handleDeselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleDeletePhoto = async () => {
    if (!confirmDelete) return;
    try {
      await PhotoGalleryService.deletePhoto(confirmDelete.id);
      setConfirmDelete(null);
      selectedIds.delete(confirmDelete.id);
      setSelectedIds(new Set(selectedIds));
      loadPhotos();
    } catch (err) {
      console.error("Failed to delete photo", err);
    }
  };

  const handleSetCover = async () => {
    const firstSelected = photos.find((p) => selectedIds.has(p.id));
    if (!firstSelected) return;
    try {
      await PhotoGalleryService.updateAlbum(album.id, {
        coverPhotoUrl: firstSelected.thumbnailUrl || firstSelected.s3Url,
      });
      onAlbumUpdated();
    } catch (err) {
      console.error("Failed to set cover", err);
    }
  };

  const handleRemoveFromAlbum = async () => {
    const ids = Array.from(selectedIds);
    try {
      for (const photoId of ids) {
        await PhotoGalleryService.removePhotoFromAlbum(album.id, photoId);
      }
      setSelectedIds(new Set());
      loadPhotos();
    } catch (err) {
      console.error("Failed to remove photos from album", err);
    }
  };

  const handleTogglePublish = async () => {
    try {
      await PhotoGalleryService.updateAlbum(album.id, {
        isPublished: album.isPublished ? 0 : 1,
      });
      onAlbumUpdated();
    } catch (err) {
      console.error("Failed to toggle publish", err);
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center gap-3 mb-1">
        <h5 className="mb-0">{album.name}</h5>
        <button
          onClick={handleTogglePublish}
          style={{
            background: album.isPublished ? "#198754" : "#6c757d",
            color: "var(--text-on-brand)",
            border: "none",
            borderRadius: 12,
            padding: "3px 12px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {album.isPublished ? "Live" : "Draft"}
        </button>
      </div>
      {album.description && (
        <p className="text-muted" style={{ fontSize: 14 }}>
          {album.description}
        </p>
      )}

      {successMsg && (
        <div
          style={{
            background: "#d1e7dd",
            color: "#0f5132",
            padding: "8px 14px",
            borderRadius: 6,
            fontSize: 13,
            marginBottom: 10,
          }}
        >
          {successMsg}
        </div>
      )}

      <PhotoUploadZone albumId={album.id} onUploadComplete={loadPhotos} />

      <div className="mt-3">
        {loading ? (
          <div className="text-center py-4 text-muted">Loading photos...</div>
        ) : (
          <PhotoGrid
            photos={photos}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            onDelete={(photo) => setConfirmDelete(photo)}
            onViewPhoto={(photo) => setViewingPhoto(photo)}
            onTagRemoved={loadPhotos}
          />
        )}
      </div>

      {/* Actions bar when photos are selected */}
      {selectedIds.size > 0 && (
        <div className="d-flex gap-2 mt-2 mb-2">
          <Button variant="secondary" size="sm" onClick={handleSetCover}>
            Set as Cover
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowAddToAlbum(true)}
          >
            Add to Another Album
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowShareToVoilaBoard(true)}
          >
            Share to VoilaBoard
          </Button>
          <Button variant="danger" size="sm" onClick={handleRemoveFromAlbum}>
            Remove from Album
          </Button>
        </div>
      )}

      {/* Tagging panel */}
      {selectedIds.size > 0 && (
        <TaggingPanel
          selectedPhotoIds={Array.from(selectedIds)}
          onTagsApplied={() => {
            setSelectedIds(new Set());
            loadPhotos();
            setSuccessMsg("Tags applied successfully");
            setTimeout(() => setSuccessMsg(null), 3000);
          }}
        />
      )}

      {/* Add to Album Dialog */}
      <AddToAlbumDialog
        show={showAddToAlbum}
        photoIds={Array.from(selectedIds)}
        currentAlbumId={album.id}
        onHide={() => setShowAddToAlbum(false)}
        onDone={() => {
          setShowAddToAlbum(false);
          setSelectedIds(new Set());
        }}
      />

      {/* Share to VoilaBoard Dialog */}
      <ShareToVoilaBoardDialog
        show={showShareToVoilaBoard}
        photoIds={Array.from(selectedIds)}
        onHide={() => setShowShareToVoilaBoard(false)}
        onDone={(msg) => {
          setShowShareToVoilaBoard(false);
          setSelectedIds(new Set());
          setSuccessMsg(msg);
          setTimeout(() => setSuccessMsg(null), 4000);
        }}
      />

      {/* Delete Confirmation */}
      <Modal show={!!confirmDelete} onHide={() => setConfirmDelete(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Photo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this photo? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeletePhoto}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Lightbox */}
      {viewingPhoto && (
        <div
          className="lightbox-overlay"
          onClick={() => setViewingPhoto(null)}
        >
          <button
            className="lightbox-close"
            onClick={() => setViewingPhoto(null)}
          >
            x
          </button>
          <img
            src={viewingPhoto.s3Url}
            alt={viewingPhoto.originalFilename || ""}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default AlbumPhotosView;
